import React from "react";
import { styled } from "@mui/material/styles";

const Root = styled("div")({
  display: "flex",
  flexShrink: 0,
  position: "relative",
});
const Content = styled("div")({
  height: "100%",
  overflow: "hidden",
  width: "100%",
});
const Handle = styled("div")(({ theme }) => ({
  bottom: 0,
  cursor: "ew-resize",
  position: "absolute",
  right: -6,
  top: 0,
  touchAction: "none",
  width: 12,
  zIndex: 9,
  "&::after": {
    backgroundColor: theme.palette.divider,
    bottom: 0,
    content: '""',
    position: "absolute",
    right: 5,
    top: 0,
    transition: "background-color 0.15s ease, width 0.15s ease",
    width: 2,
  },
  "&:hover::after": {
    backgroundColor: theme.palette.primary.main,
    width: 3,
  },
}));

type ResizablePanelProps = React.PropsWithChildren<{
  className?: string;
  initialWidth?: number;
  minWidth?: number;
  minRemainingWidth?: number;
}>;

export default function ResizablePanel({
  children,
  className,
  initialWidth = 450,
  minWidth = 375,
  minRemainingWidth = 320,
}: Readonly<ResizablePanelProps>) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const handleRef = React.useRef<HTMLDivElement | null>(null);
  const pointerIdRef = React.useRef<number | null>(null);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(initialWidth);
  const widthRef = React.useRef(initialWidth);

  const applyWidth = React.useCallback((nextWidth: number) => {
    widthRef.current = nextWidth;
    if (rootRef.current) {
      rootRef.current.style.width = `${nextWidth}px`;
    }
  }, []);

  React.useLayoutEffect(() => {
    applyWidth(Math.max(initialWidth, minWidth));
  }, [applyWidth, initialWidth, minWidth]);

  const getMaxWidth = React.useCallback(() => {
    if (!rootRef.current?.parentElement) return Number.POSITIVE_INFINITY;

    const parentRect = rootRef.current.parentElement.getBoundingClientRect();
    const rootRect = rootRef.current.getBoundingClientRect();
    const siblingsWidth = Array.from(rootRef.current.parentElement.children)
      .filter((element) => element !== rootRef.current)
      .reduce((totalWidth, element) => {
        const styles = globalThis.getComputedStyle(element);
        const minWidthValue = Number.parseFloat(styles.minWidth);
        const flexGrow = Number.parseFloat(styles.flexGrow);
        let reservedWidth = (element).getBoundingClientRect().width;

        if (Number.isFinite(minWidthValue) && minWidthValue > 0) {
          reservedWidth = minWidthValue;
        } else if (Number.isFinite(flexGrow) && flexGrow > 0) {
          reservedWidth = 0;
        }

        const marginLeft = Number.parseFloat(styles.marginLeft);
        const marginRight = Number.parseFloat(styles.marginRight);

        return totalWidth + reservedWidth + marginLeft + marginRight;
      }, 0);

    return Math.max(
      minWidth,
      Math.min(
        parentRect.right - rootRect.left,
        parentRect.width - siblingsWidth - minRemainingWidth,
      ),
    );
  }, [minRemainingWidth, minWidth]);

  const stopDragging = React.useCallback((handle = handleRef.current) => {
    if (pointerIdRef.current !== null && handle?.hasPointerCapture(pointerIdRef.current)) {
      handle.releasePointerCapture(pointerIdRef.current);
    }
    pointerIdRef.current = null;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
  }, []);

  const handlePointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!rootRef.current) return;

    pointerIdRef.current = event.pointerId;
    startXRef.current = event.clientX;
    startWidthRef.current = rootRef.current.getBoundingClientRect().width;

    event.currentTarget.setPointerCapture(event.pointerId);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    event.preventDefault();
  }, []);

  const handlePointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId || !rootRef.current) return;

    const nextWidth = Math.min(
      Math.max(minWidth, startWidthRef.current + (event.clientX - startXRef.current)),
      getMaxWidth(),
    );
    applyWidth(nextWidth);
  }, [applyWidth, getMaxWidth, minWidth]);

  React.useEffect(() => {
    const handleWindowResize = () => {
      if (!rootRef.current) return;

      const nextWidth = Math.min(widthRef.current, getMaxWidth());
      applyWidth(nextWidth);
    };

    globalThis.addEventListener("resize", handleWindowResize);
    return () => globalThis.removeEventListener("resize", handleWindowResize);
  }, [applyWidth, getMaxWidth]);

  React.useEffect(() => () => {
    if (pointerIdRef.current !== null) {
      stopDragging();
    }
  }, [stopDragging]);

  const handlePointerUp = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    stopDragging(event.currentTarget);
  }, [stopDragging]);

  const handlePointerCancel = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    stopDragging(event.currentTarget);
  }, [stopDragging]);

  return (
    <Root className={className} ref={rootRef}>
      <Content>{children}</Content>
      <Handle
        onLostPointerCapture={(event) => {
          if (pointerIdRef.current === event.pointerId) {
            stopDragging(event.currentTarget);
          }
        }}
        onPointerDown={handlePointerDown}
        onPointerCancel={handlePointerCancel}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        ref={handleRef}
      />
    </Root>
  );
}
